import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebase';
import { signOut } from 'firebase/auth';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
  getDoc,
  writeBatch
} from 'firebase/firestore';

import { useAuth } from '../../context/AuthContext';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { trackEvent } from "../../utils/analytics";
import { getFunctions, httpsCallable } from 'firebase/functions';

function OrgDashboard() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  const [activeTab, setActiveTab] = useState('needs');
  const [stats, setStats] = useState({
    openNeeds: 0,
    pendingNeeds: 0,
    fundedNeeds: 0,
    totalNeeds: 0,
    incomingOrders: 0,
    receivedOrders: 0
  });

  const [recentNeeds, setRecentNeeds] = useState([]);
  const [openOrders, setOpenOrders] = useState([]);
  const [receivedOrders, setReceivedOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingReceived, setMarkingReceived] = useState({});
  const [logoUrl, setLogoUrl] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [needTypeFilter, setNeedTypeFilter] = useState('all');

  const filteredNeeds = recentNeeds.filter(n => {
    if (needTypeFilter === 'all') return true;
    return (n.type || 'supply') === needTypeFilter;
  });

  useEffect(() => {
    if (userProfile) fetchData();
  }, [userProfile]);

  async function fetchData() {
    try {
      const orgDocSnap = await getDoc(doc(db, 'organizations', auth.currentUser.uid));
      const orgData = orgDocSnap.exists() ? orgDocSnap.data() : {};
      setLogoUrl(orgData.logoUrl || '');

      const needsQ = query(
        collection(db, 'needs'),
        where('orgId', '==', auth.currentUser.uid)
      );

      const ordersQ = query(
        collection(db, 'orders'),
        where('orgId', '==', auth.currentUser.uid),
        where('status', 'in', ['ordered', 'shipped', 'delivered', 'received'])
      );

      const [needsSnap, ordersSnap] = await Promise.all([
        getDocs(needsQ),
        getDocs(ordersQ)
      ]);

      const needs = needsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      const orders = ordersSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const aTime =
            a.targetDeliveryDate?.toDate?.()?.getTime?.() ||
            a.orderedAt?.toDate?.()?.getTime?.() ||
            0;
          const bTime =
            b.targetDeliveryDate?.toDate?.()?.getTime?.() ||
            b.orderedAt?.toDate?.()?.getTime?.() ||
            0;
          return aTime - bTime;
        });

      const activeNeeds = needs.filter(n => n.status !== 'split');
      const openNeeds = activeNeeds.filter(n => n.status === 'open').length;
      const pendingNeeds = activeNeeds.filter(n => n.status === 'pending_match').length;
      const fundedNeeds = activeNeeds.filter(n => n.status === 'funded').length;

      setStats({
        openNeeds,
        pendingNeeds,
        fundedNeeds,
        totalNeeds: activeNeeds.length,
        incomingOrders: orders.filter(o =>
          o.status === 'ordered' ||
          o.status === 'shipped' ||
          o.status === 'delivered'
        ).length,
        receivedOrders: orders.filter(o => o.status === 'received').length
      });

      setRecentNeeds(activeNeeds);

      setOpenOrders(
        orders.filter(o =>
          o.status === 'ordered' ||
          o.status === 'shipped' ||
          o.status === 'delivered'
        )
      );

      setReceivedOrders(
        orders
          .filter(o => o.status === 'received')
          .sort((a, b) => {
            const aTime = a.receivedAt?.toDate?.()?.getTime?.() || 0;
            const bTime = b.receivedAt?.toDate?.()?.getTime?.() || 0;
            return bTime - aTime;
          })
      );
    } catch (err) {
      console.error('Error fetching org data:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSignOut() {
    await signOut(auth);
    navigate('/org');
  }

  async function markOrderReceived(orderId, subscriptionId) {
    try {
      setMarkingReceived(prev => ({ ...prev, [orderId]: true }));

      const batch = writeBatch(db);

      batch.update(doc(db, 'orders', orderId), {
        status: 'received',
        receivedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      batch.update(doc(db, 'subscriptions', subscriptionId), {
        orderStatus: 'received',
        receivedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      await batch.commit();

      const recordCommunityImpact = httpsCallable(getFunctions(), "recordCommunityImpact");

await recordCommunityImpact({
  orderId,
  subscriptionId
});

      trackEvent("order_received_confirmed", {
        order_id: orderId,
        subscription_id: subscriptionId
      });

      await fetchData();
    } catch (err) {
      console.error('Error marking order received:', err);
      alert('Error marking order received.');
    } finally {
      setMarkingReceived(prev => ({ ...prev, [orderId]: false }));
    }
  }

  async function handleLogoUpload(file) {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Please keep logo files under 5 MB.');
      return;
    }

    try {
      setUploadingLogo(true);

      const storage = getStorage();
      const ext = file.name.split('.').pop() || 'png';
      const storageRef = ref(storage, `org-logos/${auth.currentUser.uid}.${ext}`);

      await uploadBytes(storageRef, file, {
        contentType: file.type
      });

      const url = await getDownloadURL(storageRef);

      await updateDoc(doc(db, 'organizations', auth.currentUser.uid), {
        logoUrl: url,
        logoUpdatedAt: serverTimestamp()
      });

      setLogoUrl(url);
      alert('Logo updated.');
    } catch (err) {
      console.error('Error uploading logo:', err);
      alert('Error uploading logo.');
    } finally {
      setUploadingLogo(false);
    }
  }

  function fmtDate(ts) {
    if (!ts) return '—';
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit'
    });
  }

  const priorityColors = {
    critical: { bg: '#fee2e2', color: '#991b1b' },
    core: { bg: '#fef3c7', color: '#92400e' },
    supplemental: { bg: '#e0f2fe', color: '#075985' }
  };

  const statusColors = {
    open: { bg: '#d1fae5', color: '#065f46' },
    pending_match: { bg: '#fef3c7', color: '#92400e' },
    paused: { bg: '#f3f4f6', color: '#6b7280' },
    funded: { bg: '#dbeafe', color: '#1e40af' },
    discontinued: { bg: '#fee2e2', color: '#991b1b' },
    ordered: { bg: '#dbeafe', color: '#1e40af' },
    delivered: { bg: '#d1fae5', color: '#065f46' },
    shipped: { bg: '#dbeafe', color: '#1e40af' },
    received: { bg: '#e0f2fe', color: '#075985' }
  };

  const typeColors = {
    supply: { bg: '#e0f2fe', color: '#075985' },
    sponsorship: { bg: '#f3e8ff', color: '#6b21a8' }
  };

  const s = {
    page: {
      minHeight: '100vh',
      background: '#f9fafb',
      fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, sans-serif'
    },
    header: {
      background: 'white',
      borderBottom: '1px solid #eee',
      padding: '0 2rem',
      height: '64px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      position: 'sticky',
      top: 0,
      zIndex: 100
    },
    logo: {
      fontSize: '1.2rem',
      fontWeight: 700,
      color: '#1e3a5f',
      cursor: 'pointer'
    },
    nav: { display: 'flex', gap: '2rem' },
    navLink: {
      cursor: 'pointer',
      fontSize: '0.9rem',
      color: '#555',
      padding: '0.5rem 0',
      borderBottom: '2px solid transparent'
    },
    navActive: {
      color: '#1e3a5f',
      fontWeight: 600,
      borderBottomColor: '#1e3a5f'
    },
    userRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem'
    },
    orgName: { fontSize: '0.9rem', color: '#333' },
    btnSignout: {
      background: 'transparent',
      border: '1px solid #ddd',
      padding: '0.4rem 0.875rem',
      borderRadius: '6px',
      fontSize: '0.85rem',
      cursor: 'pointer',
      color: '#555'
    },
    main: {
      maxWidth: '960px',
      margin: '0 auto',
      padding: '2.5rem 2rem'
    },
    welcome: { marginBottom: '2rem' },
    welcomeH1: {
      fontSize: '1.8rem',
      fontWeight: 700,
      marginBottom: '0.25rem'
    },
    welcomeP: { color: '#555', fontSize: '0.95rem' },
    heroRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      gap: '1.5rem'
    },
    heroLeft: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '1rem'
    },
    logoBox: {
      width: '96px',
      height: '96px',
      background: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      flexShrink: 0
    },
    logoImg: {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      display: 'block'
    },
    logoPlaceholder: {
      fontSize: '0.8rem',
      color: '#888',
      textAlign: 'center',
      padding: '0.5rem'
    },
    logoControls: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      marginTop: '0.25rem'
    },
    uploadBtn: {
      background: 'white',
      color: '#1e3a5f',
      border: '2px solid #1e3a5f',
      padding: '0.55rem 1rem',
      borderRadius: '8px',
      fontSize: '0.85rem',
      cursor: 'pointer',
      fontWeight: 600,
      whiteSpace: 'nowrap',
      display: 'inline-block'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
      gap: '1rem',
      marginBottom: '2rem'
    },
    statCard: {
      background: 'white',
      borderRadius: '12px',
      padding: '1.25rem 1.5rem',
      border: '1px solid #eee'
    },
    statNum: {
      fontSize: '2rem',
      fontWeight: 700,
      color: '#1e3a5f',
      lineHeight: 1,
      marginBottom: '0.25rem'
    },
    statLabel: { fontSize: '0.85rem', color: '#888' },
    tabs: {
      display: 'flex',
      gap: '1rem',
      marginBottom: '1.5rem',
      borderBottom: '1px solid #eee'
    },
    tab: {
      background: 'transparent',
      border: 'none',
      borderBottom: '2px solid transparent',
      padding: '0.65rem 0.2rem',
      cursor: 'pointer',
      fontSize: '0.95rem',
      fontWeight: 500,
      color: '#666'
    },
    tabActive: {
      color: '#1e3a5f',
      borderBottomColor: '#1e3a5f',
      fontWeight: 700
    },
    filterRow: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '1rem',
      flexWrap: 'wrap'
    },
    filterBtn: {
      border: 'none',
      borderRadius: '999px',
      padding: '0.45rem 0.9rem',
      cursor: 'pointer',
      fontWeight: 600,
      fontSize: '0.85rem'
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1rem'
    },
    sectionTitle: { fontSize: '1.1rem', fontWeight: 700 },
    sectionLink: {
      fontSize: '0.875rem',
      color: '#1e3a5f',
      cursor: 'pointer',
      fontWeight: 500
    },
    needRow: {
      background: 'white',
      borderRadius: '12px',
      padding: '1rem 1.25rem',
      border: '1px solid #eee',
      marginBottom: '0.75rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '1rem'
    },
    needLeft: { flex: 1 },
    needBadges: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '0.35rem',
      flexWrap: 'wrap',
      alignItems: 'center'
    },
    badge: {
      display: 'inline-block',
      padding: '0.15rem 0.5rem',
      borderRadius: '20px',
      fontSize: '0.72rem',
      fontWeight: 700,
      textTransform: 'uppercase'
    },
    needName: { fontSize: '1rem', fontWeight: 700 },
    needImpact: { fontSize: '0.85rem', color: '#555' },
    needRight: { textAlign: 'right' },
    needPrice: {
      fontSize: '1.1rem',
      fontWeight: 700,
      color: '#1e3a5f'
    },
    needPricePending: {
      fontSize: '0.9rem',
      color: '#888',
      fontStyle: 'italic'
    },
    needSponsors: { fontSize: '0.8rem', color: '#888' },
    orderCard: {
      background: 'white',
      borderRadius: '12px',
      padding: '1rem 1.25rem',
      border: '1px solid #eee',
      marginBottom: '0.75rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '1rem'
    },
    orderLeft: { flex: 1 },
    orderTitle: { fontSize: '1rem', fontWeight: 700, marginBottom: '0.25rem' },
    orderMeta: { fontSize: '0.85rem', color: '#555', marginBottom: '0.25rem' },
    orderSubMeta: { fontSize: '0.82rem', color: '#888' },
    orderRight: { textAlign: 'right', minWidth: '180px' },
    btnPrimary: {
      background: '#1e3a5f',
      color: 'white',
      border: 'none',
      padding: '0.75rem 1.5rem',
      borderRadius: '8px',
      fontSize: '0.95rem',
      cursor: 'pointer',
      fontWeight: 600
    },
    btnApprove: {
      background: '#2d6a4f',
      color: 'white',
      border: 'none',
      padding: '0.6rem 1rem',
      borderRadius: '8px',
      fontSize: '0.85rem',
      cursor: 'pointer',
      fontWeight: 600
    },
    emptyState: {
      background: 'white',
      borderRadius: '16px',
      padding: '3rem 2rem',
      textAlign: 'center',
      border: '1px solid #eee'
    },
    emptyIcon: { fontSize: '2.5rem', marginBottom: '0.75rem' },
    emptyH2: { fontSize: '1.3rem', marginBottom: '0.5rem' },
    emptyP: {
      color: '#555',
      fontSize: '0.9rem',
      maxWidth: '420px',
      margin: '0 auto 1.25rem',
      lineHeight: 1.6
    },
    alertBox: {
      background: '#fef3c7',
      border: '1px solid #fde68a',
      borderRadius: '10px',
      padding: '0.875rem 1rem',
      marginBottom: '1.5rem',
      fontSize: '0.875rem',
      color: '#92400e'
    }
  };

  return (
    <div style={s.page}>
      <header style={s.header}>
        <div style={s.logo} onClick={() => navigate('/org')}>
          somebody.gives
        </div>

        <nav style={s.nav}>
          <span
            style={{ ...s.navLink, ...(activeTab === 'needs' ? s.navActive : {}) }}
            onClick={() => setActiveTab('needs')}
          >
            Dashboard
          </span>

          <span
            style={s.navLink}
            onClick={() => navigate('/org/needs')}
          >
            Manage needs
          </span>

          <span
            style={s.navLink}
            onClick={() => navigate('/org/profile')}
          >
            Profile
          </span>
        </nav>

        <div style={s.userRow}>
          <span style={s.orgName}>
            {userProfile?.orgName || 'Organization'}
          </span>
          <button onClick={handleSignOut} style={s.btnSignout}>
            Sign out
          </button>
        </div>
      </header>

      <main style={s.main}>
        <div style={s.welcome}>
          <div style={s.heroRow}>
            <div style={s.heroLeft}>
              <div style={s.logoBox}>
                {logoUrl ? (
                  <img src={logoUrl} alt="Organization logo" style={s.logoImg} />
                ) : (
                  <div style={s.logoPlaceholder}>No logo yet</div>
                )}
              </div>

              <div>
                <h1 style={s.welcomeH1}>
                  {userProfile?.orgName || 'Your organization'}
                </h1>
                <p style={s.welcomeP}>
                  Manage your supply needs, sponsorship opportunities, and incoming orders.
                </p>

                <div style={s.logoControls}>
                  <label style={s.uploadBtn}>
                    {uploadingLogo ? 'Uploading...' : (logoUrl ? 'Replace logo' : 'Upload logo')}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                      style={{ display: 'none' }}
                      disabled={uploadingLogo}
                      onChange={(e) => handleLogoUpload(e.target.files?.[0])}
                    />
                  </label>
                  <div style={{ fontSize: '0.78rem', color: '#888' }}>
                    PNG, JPG, WEBP, or GIF up to 5 MB
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                const url = `${window.location.origin}/o/${auth.currentUser?.uid}`;
                navigator.clipboard.writeText(url);
                alert('Your shareable link has been copied to clipboard:\n' + url);
              }}
              style={{
                background: 'white',
                color: '#1e3a5f',
                border: '2px solid #1e3a5f',
                padding: '0.6rem 1.25rem',
                borderRadius: '8px',
                fontSize: '0.875rem',
                cursor: 'pointer',
                fontWeight: 600,
                whiteSpace: 'nowrap'
              }}
            >
              🔗 Copy your shareable link
            </button>
          </div>
        </div>

        {stats.pendingNeeds > 0 && (
          <div style={s.alertBox}>
            You have {stats.pendingNeeds} need
            {stats.pendingNeeds !== 1 ? 's' : ''} pending catalog
            review. We will confirm sourcing within 1-2 business days
            and notify you when they go live to donors.
          </div>
        )}

        <div style={s.statsGrid}>
          <div style={s.statCard}>
            <div style={s.statNum}>{stats.openNeeds}</div>
            <div style={s.statLabel}>Open needs</div>
          </div>

          <div style={s.statCard}>
            <div style={s.statNum}>{stats.receivedOrders}</div>
            <div style={s.statLabel}>Received orders</div>
          </div>

          <div style={s.statCard}>
            <div style={s.statNum}>{stats.fundedNeeds}</div>
            <div style={s.statLabel}>Funded needs</div>
          </div>

          <div style={s.statCard}>
            <div style={s.statNum}>{stats.pendingNeeds}</div>
            <div style={s.statLabel}>Pending review</div>
          </div>

          <div style={s.statCard}>
            <div style={s.statNum}>{stats.incomingOrders}</div>
            <div style={s.statLabel}>Incoming orders</div>
          </div>
        </div>

        <div style={s.tabs}>
          <button
            style={{ ...s.tab, ...(activeTab === 'needs' ? s.tabActive : {}) }}
            onClick={() => setActiveTab('needs')}
          >
            Your needs
          </button>

          <button
            style={{ ...s.tab, ...(activeTab === 'orders' ? s.tabActive : {}) }}
            onClick={() => setActiveTab('orders')}
          >
            Orders
          </button>

          <button
            style={{ ...s.tab, ...(activeTab === 'received' ? s.tabActive : {}) }}
            onClick={() => setActiveTab('received')}
          >
            Received
          </button>
        </div>

        {activeTab === 'needs' && (
          <>
            <div style={s.filterRow}>
              <button
                onClick={() => setNeedTypeFilter('all')}
                style={{
                  ...s.filterBtn,
                  background: needTypeFilter === 'all' ? '#1e3a5f' : '#e5e7eb',
                  color: needTypeFilter === 'all' ? '#fff' : '#111827'
                }}
              >
                All
              </button>

              <button
                onClick={() => setNeedTypeFilter('supply')}
                style={{
                  ...s.filterBtn,
                  background: needTypeFilter === 'supply' ? '#1e3a5f' : '#e5e7eb',
                  color: needTypeFilter === 'supply' ? '#fff' : '#111827'
                }}
              >
                Supply Needs
              </button>

              <button
                onClick={() => setNeedTypeFilter('sponsorship')}
                style={{
                  ...s.filterBtn,
                  background: needTypeFilter === 'sponsorship' ? '#1e3a5f' : '#e5e7eb',
                  color: needTypeFilter === 'sponsorship' ? '#fff' : '#111827'
                }}
              >
                Sponsorships
              </button>
            </div>

            {filteredNeeds.length > 0 ? (
              <>
                <div style={s.sectionHeader}>
                  <div style={s.sectionTitle}>Your needs</div>
                  <span
                    style={s.sectionLink}
                    onClick={() => navigate('/org/needs')}
                  >
                    Manage all needs →
                  </span>
                </div>

                {filteredNeeds.map(need => {
                  const needType = need.type || 'supply';
                  const typeStyle = typeColors[needType] || typeColors.supply;

                  return (
                    <div key={need.id} style={s.needRow}>
                      <div style={s.needLeft}>
                        <div style={s.needBadges}>
                          <span style={{
                            ...s.badge,
                            background: typeStyle.bg,
                            color: typeStyle.color
                          }}>
                            {needType}
                          </span>

                          <span style={{
                            ...s.badge,
                            background: priorityColors[need.priority]?.bg,
                            color: priorityColors[need.priority]?.color
                          }}>
                            {need.priority}
                          </span>

                          <span style={{
                            ...s.badge,
                            background: statusColors[need.status]?.bg,
                            color: statusColors[need.status]?.color
                          }}>
                            {need.status === 'pending_match'
                              ? 'pending review'
                              : need.status}
                          </span>

                          <span style={{ fontSize: '0.8rem', color: '#888' }}>
                            {need.frequency}
                          </span>
                        </div>

                        <div style={s.needName}>{need.itemName}</div>

                        <div style={s.needImpact}>
                          {need.impactStatement || need.description || ''}
                        </div>
                      </div>

                      <div style={s.needRight}>
                      <div style={{ fontSize: '0.85rem', color: '#555', fontWeight: 700 }}>
                        {need.frequency}
                      </div>

                      <div style={s.needSponsors}>
                        {needType === 'sponsorship'
                          ? `${need.sponsorshipInterestCount || 0} interested donor${need.sponsorshipInterestCount !== 1 ? 's' : ''}`
                          : `${need.sponsorCount || 0} sponsor${need.sponsorCount !== 1 ? 's' : ''}`}
                      </div>
                    </div>
                    </div>
                  );
                })}
              </>
            ) : (
              !loading && (
                <div style={s.emptyState}>
                  <div style={s.emptyIcon}>📋</div>
                  <h2 style={s.emptyH2}>No needs match this filter</h2>
                  <p style={s.emptyP}>
                    Change the filter above or manage your needs to add supply needs
                    or sponsorship opportunities.
                  </p>
                  <button
                    onClick={() => navigate('/org/needs')}
                    style={s.btnPrimary}
                  >
                    Manage needs →
                  </button>
                </div>
              )
            )}
          </>
        )}

        {activeTab === 'orders' && (
          <>
            <div style={s.sectionHeader}>
              <div style={s.sectionTitle}>Incoming shipments</div>
            </div>

            {openOrders.length === 0 ? (
              <div style={s.emptyState}>
                <div style={s.emptyIcon}>📦</div>
                <h2 style={s.emptyH2}>No incoming shipments right now</h2>
                <p style={s.emptyP}>
                  When an order is placed for your organization, it will appear here
                  with expected delivery timing so your team knows what is coming.
                </p>
              </div>
            ) : (
              openOrders.map(order => (
                <div key={order.id} style={s.orderCard}>
                  <div style={s.orderLeft}>
                    <div style={s.needBadges}>
                      <span style={{
                        ...s.badge,
                        background: statusColors[order.status]?.bg,
                        color: statusColors[order.status]?.color
                      }}>
                        {order.status}
                      </span>
                    </div>

                    <div style={s.orderTitle}>{order.itemName}</div>

                    <div style={s.orderMeta}>
                      Qty: {order.quantityPerCycle || 1}
                      {order.frequency ? ` · ${order.frequency}` : ''}
                    </div>

                    <div style={s.orderMeta}>
                      Ordered: {fmtDate(order.orderedAt)} · Expected by: {fmtDate(order.targetDeliveryDate)}
                    </div>

                    {order.shippingContactName && (
                      <div style={s.orderSubMeta}>
                        Attn: {order.shippingContactName}
                      </div>
                    )}

                    {(order.shippingAddress || order.shippingCity || order.shippingState || order.shippingZip) && (
                      <div style={s.orderSubMeta}>
                        Ship to: {order.shippingAddress || ''}
                        {order.shippingCity ? `, ${order.shippingCity}` : ''}
                        {order.shippingState ? `, ${order.shippingState}` : ''}
                        {order.shippingZip ? ` ${order.shippingZip}` : ''}
                      </div>
                    )}

                    {order.shippingInstructions && (
                      <div style={s.orderSubMeta}>
                        Delivery notes: {order.shippingInstructions}
                      </div>
                    )}

                    {order.trackingNumber && (
                      <div style={s.orderSubMeta}>
                        Tracking: {order.carrier} · {order.trackingNumber}
                        {order.trackingUrl && (
                          <>
                            {' · '}
                            <a href={order.trackingUrl} target="_blank" rel="noreferrer">
                              Track package →
                            </a>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  <div style={s.orderRight}>
                    {order.vendorProductUrl ? (
                      <div style={{ marginBottom: '0.75rem' }}>
                        <a
                          href={order.vendorProductUrl}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            fontSize: '0.85rem',
                            color: '#1e3a5f',
                            fontWeight: 600,
                            textDecoration: 'none'
                          }}
                        >
                          Product link →
                        </a>
                      </div>
                    ) : (
                      <div style={{ marginBottom: '0.75rem', fontSize: '0.82rem', color: '#888' }}>
                        No product link
                      </div>
                    )}

                    <button
                      style={{
                        ...s.btnApprove,
                        opacity: markingReceived[order.id] ? 0.6 : 1
                      }}
                      disabled={!!markingReceived[order.id]}
                      onClick={() => markOrderReceived(order.id, order.subscriptionId)}
                    >
                      {markingReceived[order.id] ? 'Saving...' : 'Mark received'}
                    </button>
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {activeTab === 'received' && (
          <>
            <div style={s.sectionHeader}>
              <div style={s.sectionTitle}>Received deliveries</div>
            </div>

            {receivedOrders.length === 0 ? (
              <div style={s.emptyState}>
                <div style={s.emptyIcon}>✅</div>
                <h2 style={s.emptyH2}>No received deliveries yet</h2>
                <p style={s.emptyP}>
                  Once your team marks shipments as received, they will remain here
                  as a history of fulfilled needs.
                </p>
              </div>
            ) : (
              receivedOrders.map(order => (
                <div key={order.id} style={s.orderCard}>
                  <div style={s.orderLeft}>
                    <div style={s.needBadges}>
                      <span style={{
                        ...s.badge,
                        background: statusColors.received.bg,
                        color: statusColors.received.color
                      }}>
                        received
                      </span>
                    </div>

                    <div style={s.orderTitle}>{order.itemName}</div>

                    <div style={s.orderMeta}>
                      {order.donorRecognition?.displayText || 'A local donor'} helped provide this need.
                    </div>

                    <div style={s.orderMeta}>
                      Qty: {order.quantityPerCycle || 1}
                      {order.frequency ? ` · ${order.frequency}` : ''}
                    </div>

                    <div style={s.orderMeta}>
                      Ordered: {fmtDate(order.orderedAt)} · Received: {fmtDate(order.receivedAt)}
                    </div>

                    {order.impactStatement && (
                      <div style={s.orderSubMeta}>
                        Impact: {order.impactStatement}
                      </div>
                    )}
                  </div>

                  <div style={s.orderRight}>
                    {order.vendorProductUrl ? (
                      <a
                        href={order.vendorProductUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          fontSize: '0.85rem',
                          color: '#1e3a5f',
                          fontWeight: 600,
                          textDecoration: 'none'
                        }}
                      >
                        Product link →
                      </a>
                    ) : (
                      <div style={{ fontSize: '0.82rem', color: '#888' }}>
                        No product link
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default OrgDashboard;